import { getDeviceType } from "@b2bportal/core-utilities";
import {
  type AvailabilityRequest,
  AvailabilityRequestEnum,
  type AvailabilityResponse,
  type GuestsSelection,
  LodgingCollection,
  LodgingSelectionEnum,
  Platform,
} from "@b2bportal/lodging-api";
import {
  type GetHotelsForTileRequest,
  type GetHotelsForTileResponse,
  type GetTilesResponse,
  rbcDealTilesApi,
} from "@b2bportal/rbc-deal-tiles-api";
import { axiosInstance } from "@hopper-b2b/api";
import { handleAxiosError, hotelsApi } from "@overrides/utilities";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { fetchAvailabilityAllPages } from "./availabilityPaging";

export const OFFER_LODGING_ID_LIMIT = 30;

export interface OfferAvailabilityArgs {
  lodgingIds: string[];
  fromDate: string;
  untilDate: string;
  guests: GuestsSelection;
  rooms: number;
}

export const hotelFundedOffersApi = createApi({
  reducerPath: "hotelFundedOffersApi",
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    fetchHotelFundedOffers: builder.query<GetTilesResponse, void>({
      queryFn: async () => {
        try {
          const response = await rbcDealTilesApi(
            axiosInstance
          ).apiV0DealTilesTilesPost({ limit: 4 });
          return { data: response.data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
    // Resolve the lodging IDs for a destination tile (marketId + countryCode),
    // used by the hotel funded offers results page.
    fetchHotelsForTile: builder.query<
      GetHotelsForTileResponse,
      GetHotelsForTileRequest
    >({
      queryFn: async (request) => {
        try {
          const response = await rbcDealTilesApi(
            axiosInstance
          ).apiV0DealTilesHotelsForTilePost({
            limit: OFFER_LODGING_ID_LIMIT,
            ...request,
          });
          return { data: response.data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
    // Availability for a tile's lodging IDs, using the standard hotels
    // availability endpoint with a LodgingIds selection.
    fetchOfferAvailability: builder.query<
      AvailabilityResponse,
      OfferAvailabilityArgs
    >({
      queryFn: async ({ lodgingIds, fromDate, untilDate, guests, rooms }) => {
        try {
          const { isDesktopAndUp } = getDeviceType();
          const request: AvailabilityRequest = {
            progressiveConfig: {},
            stayDates: { from: fromDate, until: untilDate },
            guests,
            platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
            lodgingSelection: {
              lodgingIds,
              preserveOrder: true,
              LodgingSelection: LodgingSelectionEnum.LodgingIds,
            },
            rooms: { numberOfRooms: rooms },
            lodgingCollection: LodgingCollection.NoCollection,
            excludeNonLuxuryLodgings: false,
            AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
          };
          const data = await fetchAvailabilityAllPages(request, async (req) => {
            const response = await hotelsApi.apiV0HotelsAvailabilityPost(req);
            return response.data;
          });
          return { data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
  }),
});

export const {
  useFetchHotelFundedOffersQuery,
  useFetchHotelsForTileQuery,
  useFetchOfferAvailabilityQuery,
} = hotelFundedOffersApi;
